/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import FormGroup from "@mui/material/FormGroup";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import Tracking_History from "../tracking_history";
import ActivityTimeline from "components/ActivityTimeline";

import { getPackageHistory, getParcelBoxes } from "network/service";

const boxStatusTitles = {
    0: "Pending",
    5: "Picked Up",
    1: "Intransit",
    2: "On Hold",
    3: "Delivered",
    4: "Issue",
};

const boxStatusColors = {
    0: "#9e9e9e",
    5: "#2196f3",
    1: "#ff9800",
    2: "#f44336",
    3: "#4caf50",
    4: "#f44336",
};

function HistoryDialog(props) {
    const [notiDetails, setShowDetails] = useState({ show: false });
    const [loading, setLoading] = useState(false);
    const [tracks, setTracks] = useState([]);
    const [boxes, setBoxes] = useState([]);
    const [activeTab, setActiveTab] = useState(0); // 0 = package-level, 1+ = box index

    const closeSB = () => setShowDetails((prev) => ({ ...prev, show: false }));

    useEffect(() => {
        setLoading(true);
        Promise.all([
            getPackageHistory(props.details._id),
            getParcelBoxes(props.details._id),
        ])
            .then(([histResp, boxResp]) => {
                setTracks(histResp.data);
                setBoxes(boxResp.data);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    }, []);

    // Package-level histories have no box reference; box histories carry a box ID
    const getTabHistories = () => {
        if (activeTab === 0) {
            return tracks.filter((t) => !t.box);
        }
        const box = boxes[activeTab - 1];
        if (!box) return [];
        return tracks.filter((t) => t.box === box._id);
    };

    // The last tab is the shipment's activity log (who did what).
    const activityTab = boxes.length + 1;
    const isActivity = activeTab === activityTab;
    const activeBox = activeTab > 0 && !isActivity ? boxes[activeTab - 1] : null;

    const renderSB = (
        <MDSnackbar
            color={notiDetails.color}
            icon={notiDetails.icon}
            title={notiDetails.title}
            content={notiDetails.message}
            dateTime=""
            open={notiDetails.show}
            onClose={closeSB}
            close={closeSB}
            bgWhite
        />
    );

    return (
        <Dialog open={props.isopen} maxWidth="sm" fullWidth onClose={props.handleclose}>
            <DialogContent>
                <FormGroup>
                    <MDBox component="form" noValidate width="100%">
                        <MDTypography variant="h5" fontWeight="bold" color="dark">
                            Tracking History
                        </MDTypography>

                        {props.details.tracking_number && (
                            <MDTypography variant="body2" color="text" mb={1}>
                                AWB: <strong>{props.details.tracking_number}</strong>
                            </MDTypography>
                        )}

                        {loading ? (
                            <MDBox display="flex" justifyContent="center" py={5}>
                                <CircularProgress />
                            </MDBox>
                        ) : (
                            <>
                                {/* ── Tabs ── */}
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, val) => setActiveTab(val)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}
                                >
                                    <Tab label="Package" />
                                    {boxes.map((box, index) => (
                                        <Tab
                                            key={box._id}
                                            label={
                                                <MDBox display="flex" alignItems="center" sx={{ gap: 0.75 }}>
                                                    <span>Box {index + 1}</span>
                                                    <Chip
                                                        label={boxStatusTitles[box.status !== undefined ? box.status : 0]}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: boxStatusColors[box.status !== undefined ? box.status : 0],
                                                            color: "white",
                                                            fontSize: "0.6rem",
                                                            height: 18,
                                                        }}
                                                    />
                                                </MDBox>
                                            }
                                        />
                                    ))}
                                    <Tab label="Activity" />
                                </Tabs>

                                {/* ── Box info panel (shown when a box tab is active) ── */}
                                {activeBox && (
                                    <MDBox
                                        px={2}
                                        py={1.25}
                                        mb={1}
                                        sx={{
                                            backgroundColor: "#f5f5f5",
                                            borderRadius: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 2,
                                        }}
                                    >
                                        <MDTypography variant="caption" fontWeight="medium">
                                            Box {activeTab}
                                        </MDTypography>
                                        {activeBox.weight ? (
                                            <MDTypography variant="caption" color="text">
                                                {activeBox.weight} kg
                                            </MDTypography>
                                        ) : null}
                                        {activeBox.tracking_number ? (
                                            <MDTypography variant="caption" color="text">
                                                AWB: <strong>{activeBox.tracking_number}</strong>
                                            </MDTypography>
                                        ) : null}
                                        <Chip
                                            label={boxStatusTitles[activeBox.status !== undefined ? activeBox.status : 0]}
                                            size="small"
                                            sx={{
                                                backgroundColor: boxStatusColors[activeBox.status !== undefined ? activeBox.status : 0],
                                                color: "white",
                                                fontSize: "0.65rem",
                                            }}
                                        />
                                    </MDBox>
                                )}

                                {/* ── History list (or the activity log) ── */}
                                {isActivity ? (
                                    <MDBox pt={1} sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                                        <ActivityTimeline entityId={props.details._id} pageSize={30} compact />
                                    </MDBox>
                                ) : getTabHistories().length > 0 ? (
                                    <Tracking_History
                                        history={getTabHistories()}
                                        setShowDetails={setShowDetails}
                                    />
                                ) : (
                                    <MDBox textAlign="center" py={4}>
                                        <MDTypography variant="body2" color="text">
                                            No tracking history yet for this {activeTab === 0 ? "package" : "box"}.
                                        </MDTypography>
                                    </MDBox>
                                )}
                            </>
                        )}

                        {renderSB}
                    </MDBox>
                </FormGroup>
            </DialogContent>
        </Dialog>
    );
}

export default HistoryDialog;
